import axios from 'axios';

import { logger } from 'src/utils/logger';

let cachedAuthKey = null;
let cachedAuthPromise = null;
let trackedJsrContext = null;


/**
 * Get configuration for JSR client
 */
export const getJsrConfig = (options) => ({
    baseURL: options?.serverUrl || '',
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Suppress-Basic': 'true',
        'x-remote-domain': '1',
    }
});

/**
 * Create an axios instance for JSR API calls
 */
export const createJsrClient = (options) => {
    return axios.create(getJsrConfig(options));
};

/**
 * Clear cached JSR auth probe results (e.g. after tenant switch or logout).
 */
export const resetJsrAuthCache = () => {
    cachedAuthKey = null;
    cachedAuthPromise = null;
};

/**
 * End the current JSR browser session via GET /logout.html.
 */
export const signOutJsrSession = async ({ serverUrl }) => {
    if (!serverUrl) {
        return false;
    }

    try {
        const client = createJsrClient({ serverUrl });
        await client.get('/logout.html');
    } catch (error) {
        logger.warn('Failed to sign out JSR session:', error);
    }

    resetJsrAuthCache();
    trackedJsrContext = null;
    return true;
};

/**
 * End the previous JSR browser session when tenant or server context changes.
 * Uses module-level tracking so multiple hook instances do not duplicate logout.
 */
export const signOutPreviousJsrSessionIfNeeded = async ({ serverUrl, tenantId }) => {
    const next = { serverUrl, tenantId };
    const prev = trackedJsrContext;
    trackedJsrContext = next;

    if (!prev?.serverUrl) {
        return false;
    }

    if (prev.serverUrl === next.serverUrl && prev.tenantId === next.tenantId) {
        return false;
    }

    await signOutJsrSession({ serverUrl: prev.serverUrl });
    trackedJsrContext = next;
    return true;
};

/**
 * Ensure JSR authentication by performing a probe request to the server.
 * Caches the result to avoid redundant probes for the same serverUrl+token combination.
 */
export const buildSearchParams = ({ options, types }) => {
    const searchParams = new URLSearchParams(options || {});
    searchParams.append('limit', '0');
    (types || []).forEach((type) => searchParams.append('type', type));
    return searchParams;
};

/**
 * Build the resource URL for JSR API calls, including options, types, and token if provided.
 */
export const buildResourceUrl = ({ options, types, token }) => {
    const searchParams = buildSearchParams({ options, types });
    if (token) {
        searchParams.append('token', token);
    }
    return `/rest_v2/resources?${searchParams.toString()}`;
};

const buildServerInfoUrl = ({ token }) => {
    if (!token) return '/rest_v2/serverInfo';
    return `/rest_v2/serverInfo?token=${encodeURIComponent(token)}`;
};

const getServerInfoWithFallback = async ({ client, accessToken, allowTokenFallback }) => {
    try {
        return {
            response: await client.get(buildServerInfoUrl({ token: null })),
            tokenRequired: false,
        };
    } catch (error) {
        if (!allowTokenFallback || error?.response?.status !== 401) {
            throw error;
        }

        return {
            response: await client.get(buildServerInfoUrl({ token: accessToken })),
            tokenRequired: true,
        };
    }
};

export const ensureJsrAuthOnce = async ({ serverUrl, accessToken, options, types }) => {
    if (!serverUrl) {
        throw new Error('JSR serverUrl is required');
    }
    if (!accessToken) {
        throw new Error('Access token is not available');
    }

    const authKey = `${serverUrl}|${accessToken}`;
    if (cachedAuthPromise && cachedAuthKey === authKey) {
        return cachedAuthPromise;
    }

    cachedAuthKey = authKey;
    const authPromise = (async () => {
        const client = createJsrClient({ serverUrl });

        let bootstrappedWithToken = false;
        let probeResourceLookup = [];

        const probeKey = buildSearchParams({ options, types }).toString();

        // 1) Probe resources without token.
        try {
            const probeResponse = await client.get(buildResourceUrl({ options, types, token: null }));
            probeResourceLookup = probeResponse.data?.resourceLookup || [];
        } catch (error) {
            if (error?.response?.status === 401) {
                bootstrappedWithToken = true;
            } else {
                logger.error('JSR auth probe failed:', error);
                throw error;
            }
        }

        // 2) If required, retry probe with token query param.
        if (bootstrappedWithToken) {
            try {
                const retryResponse = await client.get(
                    buildResourceUrl({ options, types, token: accessToken }),
                );
                probeResourceLookup = retryResponse.data?.resourceLookup || [];
            } catch (error) {
                logger.error('JSR auth probe retry failed:', error);
                throw error;
            }
        }

        // 3) Determine JasperReports Server version.
        try {
            const { response: serverInfo, tokenRequired: serverInfoRequiresToken } =
                await getServerInfoWithFallback({
                    client,
                    accessToken,
                    allowTokenFallback: bootstrappedWithToken,
                });
            const version = parseInt(serverInfo.data?.version, 10);

            if (!version) {
                throw new Error('Unable to parse JasperReports Server version');
            }

            return {
                version,
                tokenRequired: serverInfoRequiresToken,
                token: accessToken,
                probeKey,
                probeResourceLookup,
            };
        } catch (error) {
            logger.error('Failed to fetch JasperReports Server info:', error);
            throw error;
        }
    })();

    let wrappedPromise = null;
    wrappedPromise = authPromise.catch((error) => {
        // Avoid poisoning the cache with a rejected promise and only clear
        // if this is still the active cached auth request.
        if (cachedAuthKey === authKey && cachedAuthPromise === wrappedPromise) {
            cachedAuthKey = null;
            cachedAuthPromise = null;
        }
        throw error;
    });
    cachedAuthPromise = wrappedPromise;

    return cachedAuthPromise;
};
